/**
 * client/src/pages/Upload.tsx — the front door for a book.
 *
 * Two ideas run this screen, both from AGENTS.md.
 *
 * **1. A refusal names what it refused and what would fix it.** "Couldn't process this file" is how
 * someone re-uploads the same file four times. So the refusals here are *specific*, and half of them
 * fire **before** the upload: a `.mobi` is refused in the file picker with "Convert with Calibre to
 * EPUB first", not after 90 seconds of a 40 MB upload over a phone's uplink. The copy comes from
 * `refusalCopy()` in `lib/api.ts`, which is the same table the server's answers are rendered through
 * — the local pre-flight and the authoritative refusal are worded identically because they are the
 * same words.
 *
 * ⚠️ The client pre-flight is a *courtesy*, never the check. `worker/src/ingest/probe.ts` is the
 * authority: it sees the bytes and it detects DRM, scanned pages and fixed layout, none of which an
 * extension can tell you. If those two ever disagree, the server is right.
 *
 * **2. Only claim what is built.** PDF is in §3.1's v1 table but §12 puts PDF ingest in **P6**, so
 * this screen refuses a `.pdf` by name and says which phase — a book that uploads, queues, and fails
 * forty seconds later teaches the user nothing. One line to delete when P6 lands, and it greps.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { BookKind } from "../../../shared/doc.ts";
import { AppFrame, IconAlert, IconBack, formatBytes } from "../App.tsx";
import { ApiError, abApi, api, refusalCopy } from "../lib/api.ts";
import type { MeResponse, UploadResult } from "../lib/api.ts";

/** §6.5: 512 MB/file. Used only until `GET /me` answers with the real configured number. */
const DEFAULT_MAX_UPLOAD = 512 * 1000 * 1000;

/**
 * ⚠️ Keep this in step with `server/src/routes/uploads.ts`'s `INGESTIBLE` and `probe.ts`'s
 * `SourceFormat`. This screen refusing a format the server accepts is invisible from the server side
 * — the request never arrives — and it went stale for exactly one phase: `.pdf` was refused here with
 * "PDF reading isn't built yet. It lands in P6" while P6's poppler pipeline was built, tested
 * (37 tests) and deployed. Nobody could upload a PDF and nothing reported a problem.
 */
const ACCEPTED = [".epub", ".pdf", ".txt", ".html", ".htm"];

const KINDS: { id: BookKind; label: string; hint: string }[] = [
  { id: "prose", label: "Prose", hint: "Novels, memoir, non-fiction — the usual case." },
  { id: "poetry", label: "Poetry", hint: "Verse lines are kept as lines; the book begins at the first poem." },
  { id: "canon", label: "Scripture", hint: "Bible, Quran — a book/chapter picker instead of a spine, and no front-matter guessing." },
  { id: "manual", label: "Manual", hint: "Reference and technical books. Structure is always confirmed by hand." },
];

type Stage = "picking" | "uploading" | "creating";

interface Refusal {
  title: string;
  fix?: string;
}

/**
 * Local pre-flight. Extension and size only — everything else needs the bytes and belongs to
 * `probe.ts`. Returns `null` when the file is worth sending.
 */
function preflight(file: File, maxBytes: number): Refusal | null {
  const name = file.name.toLowerCase();
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot) : "";

  if (ext === ".mobi" || ext === ".azw" || ext === ".azw3" || ext === ".prc") {
    return refusalCopy("mobi-unsupported")!;
  }
  if (!ACCEPTED.includes(ext)) {
    return {
      title: ext ? `We can't read ${ext} files.` : "We can't tell what that file is.",
      fix: "EPUB, PDF, plain text and HTML work today.",
    };
  }
  if (file.size > maxBytes) {
    const over = refusalCopy("file-too-large")!;
    return {
      title: `That file is ${formatBytes(file.size)} — over the ${formatBytes(maxBytes)} limit.`,
      fix: over.fix,
    };
  }
  if (file.size === 0) {
    return { title: "That file is empty.", fix: "Zero bytes. Check it opens on this device first." };
  }
  return null;
}

export function Upload() {
  const navigate = useNavigate();

  const [me, setMe] = useState<MeResponse | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [dragging, setDragging] = useState(false);

  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [kind, setKind] = useState<BookKind>("prose");
  const [rightsNote, setRightsNote] = useState("");
  const [attest, setAttest] = useState(false);

  const [stage, setStage] = useState<Stage>("picking");
  const [sent, setSent] = useState(0);
  const abortRef = useRef<(() => void) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxBytes = me?.maxUploadBytes ?? DEFAULT_MAX_UPLOAD;

  useEffect(() => {
    let live = true;
    // Quota is a fact the user needs *before* they choose a file, not after the upload is refused.
    abApi
      .me()
      .then((res) => {
        if (live) setMe(res);
      })
      .catch(() => {
        /* The screen works without it; the numbers just go unstated rather than wrong. */
      });
    return () => {
      live = false;
    };
  }, []);

  const take = useCallback(
    (f: File | null | undefined) => {
      if (!f) return;
      const no = preflight(f, maxBytes);
      if (no) {
        setRefusal(no);
        setFile(null);
        return;
      }
      setRefusal(null);
      setFile(f);
    },
    [maxBytes],
  );

  async function submit() {
    if (!file || !attest || stage !== "picking") return;
    setRefusal(null);
    setStage("uploading");
    setSent(0);

    try {
      /**
       * ⚠️ **One request, not two.** `POST /api/v1/uploads` streams the bytes, hashes them, pushes
       * them to the origin, inserts the `books` row and enqueues `ingest.book` — it returns
       * `201 {book, upload:{sha,bytes,ext}, jobId}` and there is nothing left to do.
       *
       * This screen used to upload and then `POST /api/v1/books` with the returned sha. **That route
       * does not exist and never did.** It fell through to `setNotFoundHandler`, came back
       * `404 {"error":"Not found."}`, and this catch rendered a refusal titled *"Not found."* — while
       * the book had in fact been created and was already ingesting. The user was told their upload
       * failed, was never navigated to the book, and the shelf grew a book they did not know about.
       * Nothing in `test/` covers a client↔server contract, which is how it shipped.
       *
       * ⚠️ **The metadata fields go in BEFORE the file part, and the order is load-bearing.**
       * `@fastify/multipart`'s `req.file()` resolves at the first *file* part and hands the handler a
       * `fields` object holding what has been parsed so far. Appending title/author/kind after the
       * file means the server reads them only if the parser happens to have run ahead — a race whose
       * losing side is a book silently titled from its filename. FormData preserves insertion order,
       * so putting them first makes it deterministic. (They were not sent at all before this: every
       * upload took the filename as its title and dropped the kind, the author and the rights note.)
       */
      const form = new FormData();
      if (title.trim()) form.append("title", title.trim());
      if (author.trim()) form.append("author", author.trim());
      form.append("kind", kind);
      if (rightsNote.trim()) form.append("rightsNote", rightsNote.trim());
      form.append("attest", "true");
      form.append("file", file, file.name);

      const { promise, abort } = api.uploadWithProgress<UploadResult>("/uploads", form, (loaded) =>
        setSent(loaded),
      );
      abortRef.current = abort;
      const up = await promise;
      abortRef.current = null;

      setStage("creating");
      const bookId = up?.book?.id;
      if (!bookId) {
        // The bytes are stored and the job is queued; we simply do not know which book to open. The
        // shelf is the honest destination — never a blank screen with the work done and nowhere to go.
        navigate("/", { replace: true });
        return;
      }

      // Straight to the book, where the ingest reports itself. Not back to the shelf: the thing the
      // user wants to watch is the thing they just made.
      navigate(`/b/${bookId}`, { replace: true });
    } catch (err) {
      abortRef.current = null;
      setStage("picking");
      if (err instanceof ApiError) {
        const known = refusalCopy(err.reason);
        setRefusal(
          known ?? {
            title: err.message,
            fix: err.offline ? "The upload never reached the server. Nothing was stored; try again." : undefined,
          },
        );
      } else {
        setRefusal({ title: "The upload failed." });
      }
    }
  }

  const busy = stage !== "picking";
  const pct = file && file.size > 0 ? Math.min(100, Math.round((sent / file.size) * 100)) : 0;

  return (
    <AppFrame>
      <button type="button" className="ab-back" onClick={() => navigate(-1)}>
        <IconBack />
        <span>Back</span>
      </button>

      <h1 className="ab-h1">Upload a book</h1>
      <p className="ab-lede">
        It is read once, at import: the reading order comes from the spine, sentences are split once
        and stored, and where the book begins is decided with the reason written down so you can
        disagree with it.
      </p>

      {me && (
        <p className="ab-quota">
          {formatBytes(me.usedBytes)} of {formatBytes(me.quotaBytes)} used
          <span className="ab-dot-sep"> · </span>
          {me.bookCount} of {me.maxBooks} books
          <span className="ab-dot-sep"> · </span>
          {formatBytes(maxBytes)} per file
        </p>
      )}

      {refusal && (
        <div className="ab-refusal" role="alert">
          <IconAlert />
          <div>
            <strong>{refusal.title}</strong>
            {refusal.fix && <p>{refusal.fix}</p>}
          </div>
        </div>
      )}

      {/* The drop zone is also the file picker: a label wrapping a hidden input, so a tap anywhere on
          it opens the picker on a phone (where there is no drag) and a drop works on a laptop. */}
      <div
        className={dragging ? "ab-drop is-dragging" : file ? "ab-drop has-file" : "ab-drop"}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!busy) take(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={inputRef}
          id="ab-file"
          type="file"
          className="ab-sr"
          accept={ACCEPTED.join(",")}
          disabled={busy}
          onChange={(e) => take(e.target.files?.[0])}
        />
        {file ? (
          <div className="ab-drop-file">
            <strong>{file.name}</strong>
            <span>{formatBytes(file.size)}</span>
            {!busy && (
              <button
                type="button"
                className="ab-link"
                onClick={() => {
                  setFile(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
              >
                Choose a different file
              </button>
            )}
          </div>
        ) : (
          <label htmlFor="ab-file" className="ab-drop-label">
            <strong>Drop a book here</strong>
            <span>or tap to choose one</span>
            <span className="ab-drop-formats">EPUB · PDF · plain text · HTML</span>
          </label>
        )}
      </div>

      {busy && (
        <div className="ab-progress-block">
          <div className="ab-bar">
            <span
              className={stage === "creating" ? "ab-bar-fill is-indeterminate" : "ab-bar-fill"}
              style={stage === "uploading" ? { width: `${pct}%` } : undefined}
            />
          </div>
          <p className="ab-progress-label">
            {stage === "uploading"
              ? `Uploading — ${formatBytes(sent)} of ${formatBytes(file?.size ?? 0)} (${pct}%)`
              : "Uploaded. Making the book…"}
            {stage === "uploading" && (
              <button
                type="button"
                className="ab-link"
                onClick={() => {
                  abortRef.current?.();
                  abortRef.current = null;
                }}
              >
                Cancel
              </button>
            )}
          </p>
        </div>
      )}

      <fieldset className="ab-fields" disabled={busy}>
        <legend className="ab-sr">About the book</legend>

        <div className="ab-field">
          <label htmlFor="ab-title">Title</label>
          <input
            id="ab-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Leave blank to use the title inside the book"
          />
        </div>

        <div className="ab-field">
          <label htmlFor="ab-author">Author</label>
          <input
            id="ab-author"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Read from the file if you leave it blank"
          />
        </div>

        <div className="ab-field">
          <label htmlFor="ab-kind">What kind of book</label>
          <select id="ab-kind" value={kind} onChange={(e) => setKind(e.target.value as BookKind)}>
            {KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
          <p className="ab-hint">{KINDS.find((k) => k.id === kind)?.hint}</p>
        </div>

        <div className="ab-field">
          <label htmlFor="ab-rights">Where it came from</label>
          <input
            id="ab-rights"
            value={rightsNote}
            onChange={(e) => setRightsNote(e.target.value)}
            placeholder="Optional — &ldquo;bought on Kobo&rdquo;, &ldquo;Project Gutenberg&rdquo;, &ldquo;scanned from my own copy&rdquo;"
          />
          <p className="ab-hint">
            Recorded on the book. It is what a takedown request gets answered with, and it costs one
            line now instead of an afternoon later.
          </p>
        </div>

        {/* §6.3: rightsNote + an attestation at upload. Nothing becomes world-readable without
            Karti approving it, but the attestation is what makes that approval mean something. */}
        <label className="ab-check">
          <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
          <span>
            I have the right to keep a copy of this book, and I am not going to make it public
            without owning that.
          </span>
        </label>
      </fieldset>

      <div className="ab-actions">
        <button
          type="button"
          className="ab-btn ab-btn-primary"
          disabled={!file || !attest || busy}
          onClick={() => void submit()}
        >
          {busy ? "Working…" : "Upload and read it"}
        </button>
        {!file && <span className="ab-hint">Choose a file first.</span>}
        {file && !attest && <span className="ab-hint">Tick the box above.</span>}
      </div>
    </AppFrame>
  );
}
